import type { StatsigUser } from '@statsig/react-bindings'
import { StatsigProvider, useStatsigUser } from '@statsig/react-bindings'
import type { PropsWithChildren } from 'react'
import { useEffect, useMemo } from 'react'

import envConfig from '~/configs/env'
import { ANONYMOUS_STATSIG_USER_ID, gateOverrideAdapter } from '~/features/featureFlags/gateOverrides'
import { useAuth } from '~/hooks/useAuth'

// A module constant, not an inline literal: `useClientAsyncInit` keys the
// client off the options identity, so a fresh object every render would
// rebuild the Statsig client.
const STATSIG_OPTIONS = {
  environment: {
    tier: envConfig.environmentType
  },
  // Lets the Synop-internal feature flag panel substitute gate values after
  // evaluation. Inert until something writes an override.
  overrideAdapter: gateOverrideAdapter
}

export function AuthenticatedStatsigClientProvider({ children }: PropsWithChildren) {
  const { user } = useAuth()

  const statsigUser = useMemo(() => {
    if (user) {
      return {
        userID: user.id,
        email: user.email,
        customIDs: user.organizationId ? { organizationId: user.organizationId } : undefined
      }
    }
    return { userID: ANONYMOUS_STATSIG_USER_ID }
  }, [user])

  return (
    <StatsigProvider sdkKey={envConfig.statsigClientKey} user={statsigUser} options={STATSIG_OPTIONS}>
      <StatsigUserUpdater user={statsigUser}>{children}</StatsigUserUpdater>
    </StatsigProvider>
  )
}

function StatsigUserUpdater({ children, user: userProp }: PropsWithChildren<{ user: StatsigUser }>) {
  const { user, updateUserAsync } = useStatsigUser()

  useEffect(() => {
    // If the user ID is different, update the user. This will trigger a re-fetch of the user's
    // Statsig data. It's particularly important following sign-in, because simply updating the
    // `user` prop of the `StatsigProvider` is not sufficient to initiate a re-fetch.
    if (user.userID !== userProp.userID) {
      updateUserAsync(userProp)
    }
  }, [user.userID, updateUserAsync, userProp])

  return <>{children}</>
}

export function UnauthenticatedStatsigClientProvider({ children }: PropsWithChildren) {
  return (
    <StatsigProvider sdkKey={envConfig.statsigClientKey} user={{ userID: ANONYMOUS_STATSIG_USER_ID }} options={STATSIG_OPTIONS}>
      {children}
    </StatsigProvider>
  )
}
